import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { QuillModule } from 'ngx-quill';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { sanitizeInputForCrossSiteScriptCharactersDescription, getStatusPageErrorMessage, nonWhitespaceHtmlValidator, stripHtmlToText } from '../../../../../_theme/core/_base/curd/utils/validators-util';
import { LayoutUtilsService, MessageType } from 'src/app/_theme/core/_base/curd';
import { StatusPageService } from 'src/app/_theme/core/status-page/status-page.service';
import { AuthService } from 'src/app/_theme/core/users/Auth/_services/auth.service';
import { environment } from 'src/environments/environment';
import { PostmortemPayload, PostmortemResponse } from '../../models/postmortem.model';
import { StatusPageRichTextPipe } from '../../pipes/status-page-rich-text.pipe';

type PostmortemFieldKey = 'summary' | 'rootCause' | 'impact' | 'actionItems';

interface PostmortemSection {
  key: PostmortemFieldKey;
  label: string;
  placeholder: string;
  emptyText: string;
}

@Component({
  selector: 'app-incident-postmortem',
  templateUrl: './incident-postmortem.component.html',
  styleUrls: ['./incident-postmortem.component.scss', './incident-postmortem-dark.component.scss'],
  exportAs: 'incidentPostmortem',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatTooltipModule,
    QuillModule,
    StatusPageRichTextPipe
  ]
})
export class IncidentPostmortemComponent implements OnInit, OnChanges {

  readonly messageEditorModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['link']
    ]
  };

  readonly postmortemSections: PostmortemSection[] = [
    {
      key: 'summary',
      label: 'Summary',
      placeholder: 'Brief overview of the incident and its impact...',
      emptyText: 'No summary added yet.'
    },
    {
      key: 'rootCause',
      label: 'Root Cause',
      placeholder: 'What caused this incident...',
      emptyText: 'No root cause added yet.'
    },
    {
      key: 'impact',
      label: 'Impact',
      placeholder: 'Who and what was affected...',
      emptyText: 'No impact details added yet.'
    },
    {
      key: 'actionItems',
      label: 'Action Items',
      placeholder: 'Steps to prevent recurrence...',
      emptyText: 'No action items added yet.'
    }
  ];

  @Input() incidentId?: number | string;
  @Input() hasPostmortem?: boolean | null;
  @Output() stateChange = new EventEmitter<void>();
  @Output() postmortemSaved = new EventEmitter<void>();

  postmortemForm: FormGroup;
  postmortemId?: number;
  postmortem?: PostmortemResponse | null;
  private postmortemAvailabilityOverride?: boolean;
  private savedPostmortemPayloadSnapshot = '';

  isLoading = false;
  isSaving = false;
  isPublishing = false;
  isDeleting = false;
  editingField: PostmortemFieldKey | null = null;
  private fieldEditOriginalValue = '';

  constructor(
    private readonly fb: FormBuilder,
    private readonly statusPageService: StatusPageService,
    private readonly authService: AuthService,
    private readonly layoutUtilsService: LayoutUtilsService,
    private readonly cd: ChangeDetectorRef
  ) {
    this.postmortemForm = this.fb.group({
      summary: ['', [sanitizeInputForCrossSiteScriptCharactersDescription]],
      rootCause: ['', [nonWhitespaceHtmlValidator(), sanitizeInputForCrossSiteScriptCharactersDescription]],
      impact: ['', [nonWhitespaceHtmlValidator(), sanitizeInputForCrossSiteScriptCharactersDescription]],
      resolution: ['', [sanitizeInputForCrossSiteScriptCharactersDescription]],
      lessonsLearned: ['', [sanitizeInputForCrossSiteScriptCharactersDescription]],
      actionItems: ['', [nonWhitespaceHtmlValidator(), sanitizeInputForCrossSiteScriptCharactersDescription]]
    });
    this.refreshSavedPostmortemSnapshot();
  }

  ngOnInit(): void {
    this.loadPostmortem();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const incidentChanged = changes['incidentId'] && !changes['incidentId'].firstChange;
    const availabilityChanged = changes['hasPostmortem'] && !changes['hasPostmortem'].firstChange;
    if (incidentChanged) {
      this.postmortemAvailabilityOverride = undefined;
      this.editingField = null;
      this.fieldEditOriginalValue = '';
    }
    if (incidentChanged || availabilityChanged) {
      this.loadPostmortem();
    }
  }

  get isPublished(): boolean {
    return !!this.postmortem?.isPublished;
  }

  get isFormValid(): boolean {
    return this.postmortemForm.valid;
  }

  get hasUnsavedChanges(): boolean {
    return this.getPayloadSnapshot(this.buildPostmortemPayload()) !== this.savedPostmortemPayloadSnapshot;
  }

  get hasPublishableSummary(): boolean {
    return stripHtmlToText(this.postmortemForm.get('summary')?.value).length > 0;
  }

  get canSavePostmortem(): boolean {
    return this.isFormValid
      && this.hasUnsavedChanges
      && (!this.isPublished || this.hasPublishableSummary);
  }

  get canPublishPostmortem(): boolean {
    return !!this.incidentId
      && this.isFormValid
      && this.hasPublishableSummary
      && !this.isSaving
      && !this.isPublishing
      && !this.isDeleting;
  }

  get showEmptyStateNote(): boolean {
    if (this.isLoading) {
      return false;
    }
    const availability =
      typeof this.postmortemAvailabilityOverride === 'boolean'
        ? this.postmortemAvailabilityOverride
        : this.hasPostmortem;
    return availability === false && !this.postmortemId;
  }

  get showPublishSection(): boolean {
    const availability =
      typeof this.postmortemAvailabilityOverride === 'boolean'
        ? this.postmortemAvailabilityOverride
        : this.hasPostmortem;
    return !!this.postmortemId || availability === true;
  }

  onContentChanged(): void {
    // Trigger change detection so that parent components can read validity
    this.cd.detectChanges();
    this.stateChange.emit();
  }

  startEditField(field: PostmortemFieldKey): void {
    if (this.isSaving || this.isPublishing || this.isDeleting || this.editingField) {
      return;
    }
    this.fieldEditOriginalValue = this.getFieldValue(field);
    this.editingField = field;
    this.cd.detectChanges();
  }

  finishEditField(field: PostmortemFieldKey): void {
    const control = this.postmortemForm.get(field);
    control?.markAsTouched();
    control?.updateValueAndValidity();

    if (control?.invalid) {
      this.editingField = field;
      this.stateChange.emit();
      this.cd.detectChanges();
      return;
    }

    if (this.editingField === field) {
      this.editingField = null;
      this.fieldEditOriginalValue = '';
    }
    this.stateChange.emit();
    this.cd.detectChanges();
  }

  cancelEditField(field: PostmortemFieldKey): void {
    if (this.editingField !== field) {
      return;
    }
    const control = this.postmortemForm.get(field);
    control?.setValue(this.fieldEditOriginalValue);
    control?.updateValueAndValidity();
    this.editingField = null;
    this.fieldEditOriginalValue = '';
    this.stateChange.emit();
    this.cd.detectChanges();
  }

  isEditingField(field: PostmortemFieldKey): boolean {
    return this.editingField === field;
  }

  getFieldValue(field: PostmortemFieldKey): string {
    return this.postmortemForm.get(field)?.value || '';
  }

  hasFieldValue(field: PostmortemFieldKey): boolean {
    return stripHtmlToText(this.getFieldValue(field)).length > 0;
  }

  isFieldInvalid(field: PostmortemFieldKey): boolean {
    const control = this.postmortemForm.get(field);
    return !!control?.touched && !!(control?.hasError('pattern') || control?.hasError('invalidDescription'));
  }

  loadPostmortem(): void {
    if (!this.incidentId) {
      this.isLoading = false;
      this.resetPostmortem();
      this.stateChange.emit();
      return;
    }

    if (!this.shouldFetchPostmortem()) {
      this.isLoading = false;
      this.resetPostmortem();
      this.stateChange.emit();
      return;
    }

    this.isLoading = true;
    this.stateChange.emit();
    const token = this.authService.getToken();
    this.statusPageService.getIncidentPostmortem(token, this.incidentId).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.postmortem = response;
        this.postmortemId = response?.postmortemId;
        this.editingField = null;
        this.fieldEditOriginalValue = '';
        const payload = this.buildPayloadFromResponse(response);
        this.postmortemForm.patchValue(payload);
        this.refreshSavedPostmortemSnapshot(payload);
        this.cd.detectChanges();
        this.stateChange.emit();
      },
      error: (err) => {
        this.isLoading = false;
        if (err?.status === 404) {
          this.resetPostmortem();
          this.stateChange.emit();
          return;
        }
        const errorMsg = getStatusPageErrorMessage(err, 'Error loading post-mortem');
        this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
        this.resetPostmortem();
        this.stateChange.emit();
      }
    });
  }

  savePostmortem(): void {
    if (!this.incidentId || this.isSaving || this.isPublishing) {
      return;
    }

    this.postmortemForm.markAllAsTouched();
    this.postmortemForm.get('summary')?.markAsTouched();
    if (!this.canSavePostmortem) {
      this.stateChange.emit();
      this.cd.detectChanges();
      return;
    }

    const payload = this.buildPostmortemPayload();
    this.isSaving = true;
    this.stateChange.emit();
    const token = this.authService.getToken();
    const request$ = this.getSavePostmortemRequest(token, this.incidentId, payload);

    request$.subscribe({
      next: (response) => {
        this.isSaving = false;
        this.applySavedPostmortem(response, payload);
        this.editingField = null;
        this.fieldEditOriginalValue = '';
        this.postmortemAvailabilityOverride = true;
        this.stateChange.emit();
        this.layoutUtilsService.showActionNotification('Post-mortem saved successfully', MessageType.Update, environment.TOAST_MESSAGE_TIME, true, false);
        this.postmortemSaved.emit();
        this.loadPostmortem();
      },
      error: (err) => {
        const errorMsg = getStatusPageErrorMessage(err, 'Error saving post-mortem');
        this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
        this.isSaving = false;
        this.stateChange.emit();
        this.cd.detectChanges();
      }
    });
  }

  publishPostmortem(): void {
    if (!this.incidentId || this.isPublishing) {
      return;
    }

    this.postmortemForm.get('summary')?.markAsTouched();
    if (!this.canPublishPostmortem) {
      this.stateChange.emit();
      this.cd.detectChanges();
      return;
    }

    const incidentId = this.incidentId;
    const payload = this.buildPostmortemPayload();
    this.isPublishing = true;
    this.stateChange.emit();
    const token = this.authService.getToken();
    let errorFallback = 'Error saving post-mortem';

    this.getSavePostmortemRequest(token, incidentId, payload).pipe(
      switchMap((response) => {
        this.applySavedPostmortem(response, payload);
        errorFallback = 'Error publishing post-mortem';
        return this.statusPageService.publishIncidentPostmortem(token, incidentId);
      })
    ).subscribe({
      next: (response) => {
        this.isPublishing = false;
        this.applySavedPostmortem(response, this.buildPayloadFromResponse(response));
        this.editingField = null;
        this.fieldEditOriginalValue = '';
        this.stateChange.emit();
        this.layoutUtilsService.showActionNotification('Post-mortem published', MessageType.Update, environment.TOAST_MESSAGE_TIME, true, false);
        this.loadPostmortem();
      },
      error: (err) => {
        const errorMsg = getStatusPageErrorMessage(err, errorFallback);
        this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
        this.isPublishing = false;
        this.stateChange.emit();
        this.cd.detectChanges();
      }
    });
  }

  unpublishPostmortem(): void {
    if (!this.incidentId || this.isPublishing) {
      return;
    }

    this.isPublishing = true;
    this.stateChange.emit();
    const token = this.authService.getToken();
    this.statusPageService.unpublishIncidentPostmortem(token, this.incidentId).subscribe({
      next: () => {
        this.isPublishing = false;
        this.editingField = null;
        this.fieldEditOriginalValue = '';
        this.stateChange.emit();
        this.layoutUtilsService.showActionNotification('Post-mortem unpublished', MessageType.Update, environment.TOAST_MESSAGE_TIME, true, false);
        this.loadPostmortem();
      },
      error: (err) => {
        const errorMsg = getStatusPageErrorMessage(err, 'Error unpublishing post-mortem');
        this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
        this.isPublishing = false;
        this.stateChange.emit();
        this.cd.detectChanges();
      }
    });
  }

  deletePostmortem(): void {
    if (!this.incidentId || !this.postmortemId || this.isDeleting) {
      return;
    }

    this.isDeleting = true;
    this.stateChange.emit();
    const token = this.authService.getToken();
    this.statusPageService.deleteIncidentPostmortem(token, this.incidentId).subscribe({
      next: () => {
        this.isDeleting = false;
        this.postmortemAvailabilityOverride = false;
        this.resetPostmortem();
        this.stateChange.emit();
        this.layoutUtilsService.showActionNotification('Post-mortem deleted', MessageType.Delete, environment.TOAST_MESSAGE_TIME, true, false);
      },
      error: (err) => {
        const errorMsg = getStatusPageErrorMessage(err, 'Error deleting post-mortem');
        this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
        this.isDeleting = false;
        this.stateChange.emit();
        this.cd.detectChanges();
      }
    });
  }

  private resetPostmortem(): void {
    this.postmortem = null;
    this.postmortemId = undefined;
    this.editingField = null;
    this.fieldEditOriginalValue = '';
    this.postmortemForm.reset({
      summary: '',
      rootCause: '',
      impact: '',
      resolution: '',
      lessonsLearned: '',
      actionItems: ''
    });
    this.refreshSavedPostmortemSnapshot();
    this.cd.detectChanges();
  }

  private shouldFetchPostmortem(): boolean {
    if (!this.incidentId) {
      return false;
    }
    const availability =
      typeof this.postmortemAvailabilityOverride === 'boolean'
        ? this.postmortemAvailabilityOverride
        : this.hasPostmortem;
    if (availability === false) {
      return false;
    }
    return true;
  }

  private buildPostmortemPayload(): PostmortemPayload {
    const formValue = this.postmortemForm.getRawValue();
    return {
      summary: this.normalizeRichTextPayloadValue(formValue.summary),
      rootCause: this.normalizeRichTextPayloadValue(formValue.rootCause),
      impact: this.normalizeRichTextPayloadValue(formValue.impact),
      resolution: this.normalizeRichTextPayloadValue(formValue.resolution),
      lessonsLearned: this.normalizeRichTextPayloadValue(formValue.lessonsLearned),
      actionItems: this.normalizeRichTextPayloadValue(formValue.actionItems)
    };
  }

  private buildPayloadFromResponse(response: PostmortemResponse | null | undefined): PostmortemPayload {
    return {
      summary: this.normalizeRichTextPayloadValue(response?.summary),
      rootCause: this.normalizeRichTextPayloadValue(response?.rootCause),
      impact: this.normalizeRichTextPayloadValue(response?.impact),
      resolution: this.normalizeRichTextPayloadValue(response?.resolution),
      lessonsLearned: this.normalizeRichTextPayloadValue(response?.lessonsLearned),
      actionItems: this.normalizeRichTextPayloadValue(response?.actionItems)
    };
  }

  private normalizeRichTextPayloadValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    const stringValue = String(value);
    return stripHtmlToText(stringValue).length > 0 ? stringValue : '';
  }

  private getPayloadSnapshot(payload: PostmortemPayload): string {
    return JSON.stringify(payload);
  }

  private refreshSavedPostmortemSnapshot(payload = this.buildPostmortemPayload()): void {
    this.savedPostmortemPayloadSnapshot = this.getPayloadSnapshot(payload);
    this.postmortemForm.markAsPristine();
  }

  private getSavePostmortemRequest(
    token: string,
    incidentId: number | string,
    payload: PostmortemPayload
  ): Observable<PostmortemResponse> {
    return this.postmortemId
      ? this.statusPageService.updateIncidentPostmortem(token, incidentId, payload)
      : this.statusPageService.createIncidentPostmortem(token, incidentId, payload);
  }

  private applySavedPostmortem(response: PostmortemResponse | null | undefined, payload: PostmortemPayload): void {
    if (response) {
      this.postmortem = response;
      this.postmortemId = response.postmortemId ?? this.postmortemId;
    } else if (this.postmortem) {
      this.postmortem = {
        ...this.postmortem,
        ...payload
      };
    }
    this.postmortemAvailabilityOverride = true;
    this.refreshSavedPostmortemSnapshot(payload);
  }

}
